import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { RootState } from "redux/store";
import PageLoader from "components/UIComponents/PageLoader";
import { SIGN_IN_PATH } from "utils/auth/signInPath";

/**
 * Guards the cabinet routes (spec 2026-10-20-website-api-settings, Stage 6.2).
 *
 * Signed in → the page. Signed out while the bootstrap has not settled the
 * restore yet (the first render of a hard load) → a loader, never a blank
 * page. Settled and signed out → the sign-in page, carrying this location in
 * `state.from` so a successful sign-in returns here
 * (hooks/use-return-after-sign-in).
 *
 * The decision comes from the store (`sessionRestoreSettled`), never from
 * storage: a session-looking cookie can outlive a 401 clear, and a guard that
 * read it would wait on a restore that never comes.
 */
const RequireSession = ({ children }: { children: JSX.Element }) => {
  const intl = useIntl();
  const location = useLocation();
  const isUserLoggedIn = useSelector((state: RootState) => state.userData.isUserLoggedIn);
  const restoreSettled = useSelector(
    (state: RootState) => state.userData.sessionRestoreSettled
  );

  if (isUserLoggedIn) return children;
  if (!restoreSettled) {
    return <PageLoader label={intl.formatMessage({ id: "session.restoring" })} />;
  }
  return <Navigate to={SIGN_IN_PATH} replace state={{ from: location }} />;
};

export default RequireSession;
